"use client";

import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState, ConfirmationModal } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import type {
    DashboardInput,
    Chart,
    Report,
    GridPosition,
    ReportQueryResult,
} from "@bi-studio/shared/lib";
import {
    createDashboard,
    addFolderItem,
    addDashboardItem,
    executeReport,
} from "@bi-studio/shared/apis";
import { PageActions } from "@/components/common";
import { DashboardCreateActionsButton } from "@/components/page-action-buttons";
import { DashboardCreateView } from "@/components/dashboards";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchAvailableComponents,
    selectDashboardFull
} from "@/store/slices/dashboardSlice";
import {
    setDashboardDraft,
    selectDashboardDraft
} from "@/store/slices/draftSlice";

export default function CreateDashboardPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const dashboardDraft = useAppSelector(selectDashboardDraft);

    const { 
        availableCharts: charts, 
        availableReports: reports,
        isLoadingComponents: isLoading
    } = useAppSelector(selectDashboardFull);

    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    
    // Layout and items state
    const [selectedCharts, setSelectedCharts] = React.useState<Chart[]>([]);
    const [selectedReports, setSelectedReports] = React.useState<Report[]>([]);
    const [chartLayouts, setChartLayouts] = React.useState<Map<string, GridPosition>>(new Map());
    const [reportLayouts, setReportLayouts] = React.useState<Map<string, GridPosition>>(new Map());
    const [reportData, setReportData] = React.useState<Map<string, ReportQueryResult>>(new Map());
    const [loadingReports, setLoadingReports] = React.useState<Set<string>>(new Set());

    const form = useForm<DashboardInput>({
        initialValues: {
            name: "",
            description: "",
            allowedRoles: [],
        },
        validate: {
            name: (value) => (value.trim() ? null : "Dashboard name is required"),
        },
    });

    const [isRestoreModalOpen, setIsRestoreModalOpen] = React.useState(false);

    // Handle draft restoration
    React.useEffect(() => {
        if (dashboardDraft && form.values.name === "") {
            setIsRestoreModalOpen(true);
        }
    }, []);

    const handleRestoreDraft = () => {
        form.setValues(structuredClone(dashboardDraft) as DashboardInput);
        setIsRestoreModalOpen(false);
    };

    const handleDiscardDraft = () => {
        dispatch(setDashboardDraft(null));
        setIsRestoreModalOpen(false);
    };

    // Auto-save draft
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (form.values.name) {
                dispatch(setDashboardDraft(structuredClone(form.values)));
            }
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [form.values, dispatch]);

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    // Load available items
    React.useEffect(() => {
        if (!token) return;
        dispatch(fetchAvailableComponents(token));
    }, [token, dispatch]);

    // Load report data for selected reports
    React.useEffect(() => {
        if (!token || selectedReports.length === 0) return;

        const loadReportData = async (report: Report) => {
            if (reportData.has(report.id) || loadingReports.has(report.id)) return;

            try {
                setLoadingReports((prev) => new Set(prev).add(report.id));
                const data = await executeReport(token, report.id, {
                    pagination: { page: 1, pageSize: 50 },
                });
                setReportData((prev) => new Map(prev).set(report.id, data));
            } catch (err) {
                console.error(`Failed to load data for report ${report.id}:`, err);
            } finally {
                setLoadingReports((prev) => {
                    const next = new Set(prev);
                    next.delete(report.id);
                    return next;
                });
            }
        };

        selectedReports.forEach(loadReportData);
    }, [token, selectedReports, reportData, loadingReports]);

    const handleSave = async () => {
        if (form.validate().hasErrors) return;
        if (!token) return;

        try {
            setIsSaving(true);
            setError(null);

            const created = await createDashboard(token, form.values);
            dispatch(setDashboardDraft(null)); // Clear draft on success

            // Add selected charts
            const addChartPromises = selectedCharts.map((chart) => {
                const layout = chartLayouts.get(chart.id) || { x: 0, y: 0, w: 6, h: 4 };
                return addDashboardItem(token, created.id, {
                    chartId: chart.id,
                    reportId: null,
                    layout,
                });
            });

            // Add selected reports
            const addReportPromises = selectedReports.map((report) => {
                const layout = reportLayouts.get(report.id) || { x: 0, y: 0, w: 6, h: 4 };
                return addDashboardItem(token, created.id, {
                    chartId: null,
                    reportId: report.id,
                    layout,
                });
            });

            await Promise.all([...addChartPromises, ...addReportPromises]);

            if (folderId && folderId !== "root") {
                try {
                    await addFolderItem(token, folderId, {
                        itemType: "dashboard",
                        itemId: created.id,
                    });
                } catch (err) {
                    console.error("Failed to add dashboard to folder:", err);
                }
            }

            navigate(`/dashboards/${created.id}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create dashboard");
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(folderId && folderId !== "root" ? `/folders/${folderId}` : "/folders");
    };

    if (authLoading || (isLoading && charts.length === 0 && reports.length === 0)) {
        return (
            <AppLayout onLogout={handleLogout} title="Create Dashboard" subtitle="BI Studio">
                <LoadingScreen message="Initializing workspace..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) return null;

    return (
        <AppLayout onLogout={handleLogout} title="Create Dashboard" subtitle="Workspace">
            <div className="space-y-6">
                <PageActions 
                    title="Create New Dashboard" 
                    onBack={handleCancel}
                >
                    <DashboardCreateActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && (
                    <ErrorState 
                        title="Failed to create dashboard"
                        message={error}
                        onAction={() => setError(null)}
                    />
                )}

                <DashboardCreateView 
                    isSaving={isSaving}
                    form={form}
                    charts={charts}
                    reports={reports}
                    selectedCharts={selectedCharts}
                    setSelectedCharts={setSelectedCharts}
                    selectedReports={selectedReports}
                    setSelectedReports={setSelectedReports}
                    chartLayouts={chartLayouts}
                    setChartLayouts={setChartLayouts}
                    reportLayouts={reportLayouts}
                    setReportLayouts={setReportLayouts}
                    reportData={reportData}
                    setReportData={setReportData}
                    loadingReports={loadingReports}
                    token={token || ""}
                />

                <ConfirmationModal
                    opened={isRestoreModalOpen}
                    onClose={handleDiscardDraft}
                    onConfirm={handleRestoreDraft}
                    title="Unsaved Draft Found"
                    confirmLabel="Restore"
                    cancelLabel="Discard"
                >
                    You have an unsaved dashboard draft. Would you like to restore it?
                </ConfirmationModal>
            </div>
        </AppLayout>
    );
}
